import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { CATALOG } from "../mock/mock";

const emptyForm = { name: "", company: "", gst: "", email: "", phone: "", product: "", quantity: "", message: "" };

export default function EnquiryModal({ open, onOpenChange, defaultProduct, defaultSize }) {
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      const product = defaultProduct || "";
      const message = defaultProduct
        ? `Interested in: ${defaultProduct}${defaultSize ? ` - ${defaultSize}` : ""}. Please share bulk pricing, availability and delivery timeline.`
        : "";
      setForm({ ...emptyForm, product, message });
    }
  }, [open, defaultProduct, defaultSize]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill in your name, email and phone.");
      return;
    }
    setSubmitting(true);
    // MOCK submission - saved to localStorage for the preview
    setTimeout(() => {
      const existing = JSON.parse(localStorage.getItem("enquiries") || "[]");
      existing.push({ ...form, ts: Date.now() });
      localStorage.setItem("enquiries", JSON.stringify(existing));
      setSubmitting(false);
      toast.success("Enquiry submitted! Our B2B team will reach out shortly.");
      setForm(emptyForm);
      onOpenChange(false);
    }, 700);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-[#1c2e3a] text-xl">Request a Bulk Quote</DialogTitle>
          <DialogDescription>
            Tell us about your requirement and our team will get back with a tailored quote.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
          <div className="space-y-1.5">
            <Label>Full Name *</Label>
            <Input value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Your name" />
          </div>
          <div className="space-y-1.5">
            <Label>Company</Label>
            <Input value={form.company} onChange={(e) => update("company", e.target.value)} placeholder="Company name" />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>GST Number (optional)</Label>
            <Input
              value={form.gst}
              onChange={(e) => update("gst", e.target.value.toUpperCase().slice(0, 15))}
              placeholder="e.g. 27ABCDE1234F1Z5"
              className="uppercase font-mono tracking-wide"
              maxLength={15}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Email *</Label>
            <Input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="you@company.com" />
          </div>
          <div className="space-y-1.5">
            <Label>Phone *</Label>
            <Input value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="+91 ..." />
          </div>
          <div className="space-y-1.5">
            <Label>Product</Label>
            <Select value={form.product} onValueChange={(v) => update("product", v)}>
              <SelectTrigger><SelectValue placeholder="Select a product" /></SelectTrigger>
              <SelectContent>
                {CATALOG.map((p) => <SelectItem key={p.id} value={p.displayName}>{p.displayName}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Estimated Quantity</Label>
            <Input value={form.quantity} onChange={(e) => update("quantity", e.target.value)} placeholder="e.g. 200 units" />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>Message</Label>
            <Textarea value={form.message} onChange={(e) => update("message", e.target.value)} placeholder="Sizes, firmness, timeline, delivery location..." rows={3} />
          </div>
          <div className="sm:col-span-2">
            <Button type="submit" disabled={submitting} className="w-full bg-[#1c2e3a] hover:bg-[#29424f] text-white h-11 font-semibold">
              {submitting ? "Submitting..." : "Submit Enquiry"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
